/**
 * useWorksAnalytics.js
 *
 * Analytics hook for the "3 Simple Steps" process section.
 * Mirrors useWelcomeAnalytics.js exactly — same GA4 wrapper via react-ga4,
 * same IS_PRODUCTION guard (excludes localhost + 127.0.0.1), same dev-mode
 * console.group/console.table logging so events are inspectable without
 * opening the GA4 dashboard.
 *
 * HOW TO USE:
 *   const analytics = useWorksAnalytics();
 *   analytics.trackStepClick({ stepId, stepName, destination, source });
 *
 * DEV MODE:
 *   All events are console.group'd with the [WorksAnalytics] prefix.
 *   Open browser DevTools → Console to see all events in real time.
 */

import { useCallback, useRef } from "react";
import ReactGA from "react-ga4";

// ─── Production guard ──────────────────────────────────────────────────────────
// Matches useWelcomeAnalytics.js: excludes both localhost and 127.0.0.1 since
// the CRA dev server can serve on either hostname.
const IS_PRODUCTION =
  process.env.NODE_ENV === "production" &&
  typeof window !== "undefined" &&
  window.location.hostname !== "localhost" &&
  window.location.hostname !== "127.0.0.1";

// ─── Core send helper ──────────────────────────────────────────────────────────
const send = (eventName, params = {}) => {
  // Strip undefined / null / empty values so the GA4 payload stays clean
  const cleanParams = Object.fromEntries(
    Object.entries(params).filter(([, v]) => v !== undefined && v !== null && v !== "")
  );

  if (IS_PRODUCTION) {
    ReactGA.event(eventName, cleanParams);
  } else {
    // eslint-disable-next-line no-console
    console.group(`%c[WorksAnalytics] ${eventName}`, "color:#3d6b4f;font-weight:bold;");
    // eslint-disable-next-line no-console
    if (Object.keys(cleanParams).length > 0) console.table(cleanParams);
    // eslint-disable-next-line no-console
    else console.log("(no params)");
    // eslint-disable-next-line no-console
    console.groupEnd();
  }
};

// ═══════════════════════════════════════════════════════════════════════════════
// HOOK
// ═══════════════════════════════════════════════════════════════════════════════

const useWorksAnalytics = () => {
  // Guards against firing 'works_section_view' more than once, even if the
  // section re-enters the viewport (e.g. user scrolls up and back down).
  const viewFiredRef = useRef(false);

  // ── Section lifecycle ────────────────────────────────────────────────────────

  const trackSectionView = useCallback(() => {
    if (viewFiredRef.current) return;
    viewFiredRef.current = true;
    send("works_section_view");
  }, []);

  // ── Step interactions ────────────────────────────────────────────────────────
  // source: "card" (whole card clicked) or "cta_button" (the button inside it)

  const trackStepClick = useCallback(({ stepId, stepName, destination, source = "card" }) => {
    send("works_step_click", {
      step_id: stepId,
      step_name: stepName,
      destination,
      source,
    });
  }, []);

  // ── Bottom banner ─────────────────────────────────────────────────────────────

  const trackBannerCtaClick = useCallback((destination) => {
    send("works_banner_cta_click", { destination });
  }, []);

  return {
    trackSectionView,
    trackStepClick,
    trackBannerCtaClick,
  };
};

export default useWorksAnalytics;
